// useStageForm — per-stage form aggregator. Form-archetype components
// register their `(payloadKey, value, validator)` tuple via the context
// returned here; the parent <CompoundStep> collects them and produces a
// final payload patch when the customer clicks Continue.
//
// Mechanics:
//   - The stage host calls `createStageFormRegistry()` and stashes the
//     returned registry in a ref (or React state).
//   - Form-archetype components call `useStageForm(registry)` once on
//     mount and re-register on every value/validator change.
//   - At submit time, the host calls `registry.collect()` which runs
//     every validator, returns aggregated errors or a flat patch built
//     from the registered payload keys (dotted-path expansion supported).
//
// We intentionally do NOT use React context here — keeping it as a tiny
// observable object means the host doesn't need to wrap each block in a
// Provider, and component trees stay shallow. Form archetypes accept the
// registry as a prop from the CompoundStep host.
import { useEffect } from 'react';
export function createStageFormRegistry() {
    const fields = new Map();
    return {
        set(blockId, field) {
            fields.set(blockId, field);
        },
        remove(blockId) {
            fields.delete(blockId);
        },
        collect() {
            const errors = {};
            const patch = {};
            for (const [blockId, field] of fields) {
                if (field.validator) {
                    const err = field.validator(field.value);
                    if (err)
                        errors[blockId] = err;
                }
                if (field.value !== undefined) {
                    setByPath(patch, field.payloadKey, field.value);
                }
            }
            if (Object.keys(errors).length > 0)
                return { errors };
            return { patch };
        },
        payloadKeys() {
            return Array.from(fields.values()).map((f) => f.payloadKey);
        },
    };
}
/**
 * Hook used by form-archetype components to register their current
 * value with the parent <CompoundStep>'s registry. Re-registers on every
 * value/validator change so `collect()` always sees the latest.
 */
export function useStageForm(registry, blockId, field) {
    useEffect(() => {
        if (!registry)
            return;
        registry.set(blockId, field);
        return () => registry.remove(blockId);
    }, [registry, blockId, field.payloadKey, field.value, field.validator]);
}
/** Set `obj.a.b.c = value` from a dotted path, creating intermediate objects. */
function setByPath(obj, path, value) {
    const parts = path.split('.');
    let cur = obj;
    for (let i = 0; i < parts.length - 1; i++) {
        const key = parts[i];
        const next = cur[key];
        if (typeof next !== 'object' || next === null || Array.isArray(next)) {
            cur[key] = {};
        }
        cur = cur[key];
    }
    cur[parts[parts.length - 1]] = value;
}
